// Savings Potential Report — standalone premium report.
import { savingsBasisNote } from '@/lib/savingsBasis';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { NAVY, safeText, getCurrencySymbol, formatValue } from './utils';
import { applyUnicodeFontSupport } from './fonts';
import { drawVerticalBarChart } from './charts';

export async function buildSavingsReport(api, reportName) {
  const cs = getCurrencySymbol();
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  await applyUnicodeFontSupport(doc, api, reportName);
  const pw = doc.internal.pageSize.getWidth();

  // Header
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pw, 22, 'F');
  doc.setFont('Helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(255, 255, 255);
  doc.text(safeText(reportName || 'Savings Potential Report'), 14, 14);
  doc.setFontSize(8);
  doc.setFont('Helvetica', 'normal');
  doc.text(`Generated: ${new Date().toLocaleDateString('en-GB')}`, pw - 14, 14, { align: 'right' });

  let y = 30;

  try {
    const savingsRes = await api.get('/ai/savings-summary?force=true');
    const sav = savingsRes.data;

    if (!sav.has_insights) {
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(10);
      doc.text('No savings insights available. Ensure data has been uploaded and analyzed.', 14, y);
    } else {
      doc.setTextColor(0, 0, 0);
      doc.setFont('Helvetica', 'bold');
      doc.setFontSize(11);
      doc.text('Savings Potential Summary', 14, y);
      y += 6;
      doc.setFont('Helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(80, 80, 80);
      const summaryText = safeText(sav.summary || '');
      const summaryLines = doc.splitTextToSize(summaryText, pw - 28);
      doc.text(summaryLines, 14, y);
      y += summaryLines.length * 4 + 2;
      const scopeLines = doc.splitTextToSize(safeText(savingsBasisNote(sav, cs, formatValue)), pw - 28);
      doc.setTextColor(120, 120, 120);
      doc.text(scopeLines, 14, y);
      y += scopeLines.length * 4 + 6;

      autoTable(doc, {
        startY: y,
        head: [['Savings Potential (Est.)', 'Low', 'Mid', 'High']],
        body: [[
          safeText((sav.summary || '').substring(0, 60) + '...'),
          `${cs}${formatValue(sav.total_savings_low || 0)}`,
          `${cs}${formatValue(sav.total_savings_mid || 0)}`,
          `${cs}${formatValue(sav.total_savings_high || 0)}`,
        ]],
        styles: { fontSize: 9, halign: 'center' },
        headStyles: { fillColor: [16, 124, 65] },
        margin: { left: 14, right: 14 },
      });
      y = doc.lastAutoTable.finalY + 10;

      if (sav.breakdown?.length > 0) {
        const chartData = sav.breakdown.map(b => ({ label: b.area, value: b.potential || 0 }));
        y = drawVerticalBarChart(doc, 14, y, pw - 28, 75, chartData, { title: 'Savings Breakdown by Area' });
        y += 4;

        autoTable(doc, {
          startY: y,
          head: [['Savings Area', 'Potential', 'Recommended Action']],
          body: sav.breakdown.map(b => [
            safeText(b.area),
            `${cs}${formatValue(b.potential || 0)}`,
            safeText(b.action || ''),
          ]),
          styles: { fontSize: 7, cellPadding: 2.5 },
          headStyles: { fillColor: [16, 124, 65] },
          margin: { left: 14, right: 14 },
          columnStyles: {
            0: { cellWidth: 40 },
            1: { cellWidth: 22, halign: 'center' },
            2: { cellWidth: 'auto' },
          },
        });
        y = doc.lastAutoTable.finalY + 10;
      }

      if (y > 240) { doc.addPage(); y = 15; }
      doc.setFontSize(6.5);
      doc.setTextColor(140, 140, 140);
      const disclaimer = 'Note: Savings estimates and projections are indicative - actual outcomes will depend on the depth of due diligence, quality of documentation, strength of governance, and effectiveness of planning and execution.';
      const discLines = doc.splitTextToSize(safeText(disclaimer), pw - 28);
      doc.text(discLines, 14, y);
    }
  } catch {
    doc.setTextColor(200, 0, 0);
    doc.setFontSize(10);
    doc.text('Failed to load savings data.', 14, y);
  }

  const pdfBlob = doc.output('blob');
  const url = URL.createObjectURL(pdfBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${(reportName || 'Savings_Report').replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    try { document.body.removeChild(link); URL.revokeObjectURL(url); } catch { /* noop */ }
  }, 1500);

  return pdfBlob;
}
